import { useState, useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import "./App.css";
import logo from "./logo.svg";
import { DataProvider, useData } from "./context/DataContext";
import AdminLogin from "./admin/AdminLogin";
import AdminProducts from "./admin/AdminProducts";
import AdminCategories from "./admin/AdminCategories";
import AdminContent from "./admin/AdminContent";
import AdminQuoteRequests from "./admin/AdminQuoteRequests";
import AdminReviews from "./admin/AdminReviews";
import QuoteRequestForm from "./QuoteRequestForm";

// Utility function to generate URL-safe slugs from product names
const generateSlug = (name) => {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

const scrollToPageTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
};

function AnimatedWords({ text, className = "" }) {
    return (
        <span className={`animated-words ${className}`}>
            {text.split(" ").map((word, index) => (
                <span className="animated-word-wrap" key={`${word}-${index}`}>
                    <span className="animated-word" style={{ "--word-i": index }}>{word}</span>
                </span>
            ))}
        </span>
    );
}

function MaterialMarquee({ items }) {
    const loopItems = [...items, ...items];
    return (
        <div className="material-marquee" aria-hidden="true">
            <div className="material-marquee-track">
                {loopItems.map((item, index) => (
                    <span key={`${item}-${index}`}>{item}</span>
                ))}
            </div>
        </div>
    );
}

function useScrollReveal(pageKey) {
    useEffect(() => {
        const selectors = [
            ".products-header-wrap",
            ".prod-type-card",
            ".acc-bar-inner",
            ".trade > .trade-inner",
            ".feat",
            ".trade-box",
            ".about-grid",
            ".vm-card",
            ".about-p",
            ".audience-chip",
            ".contact-grid > div",
            ".browse-header",
            ".category-header",
            ".product-card",
            ".product-detail-grid",
            ".product-detail-summary",
            ".suggested-products-section .section-container",
            ".suggested-product-card",
            ".reviews-header",
            ".review-item",
        ].join(",");

        const elements = Array.from(document.querySelectorAll(selectors));
        elements.forEach((el, index) => {
            el.classList.add("reveal");
            el.style.setProperty("--reveal-delay", `${Math.min(index % 8, 7) * 70}ms`);
        });

        if (!("IntersectionObserver" in window)) {
            elements.forEach((el) => el.classList.add("is-visible"));
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.16, rootMargin: "0px 0px -8% 0px" }
        );

        elements.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [pageKey]);
}

function useCinematicParallax(pageKey) {
    useEffect(() => {
        const root = document.documentElement;
        let frame = 0;

        const update = () => {
            frame = 0;
            const scrollY = window.scrollY;
            const maxScroll = Math.max(document.body.scrollHeight - window.innerHeight, 1);
            root.style.setProperty("--scroll-progress", `${Math.min(scrollY / maxScroll, 1)}`);
            root.style.setProperty("--scroll-y", `${scrollY}px`);

            document.querySelectorAll("[data-parallax-depth]").forEach((el) => {
                const depth = Number(el.getAttribute("data-parallax-depth") || 0.12);
                const rect = el.getBoundingClientRect();
                const viewportCenter = window.innerHeight / 2;
                const elementCenter = rect.top + rect.height / 2;
                const offset = (viewportCenter - elementCenter) * depth;
                el.style.setProperty("--parallax-y", `${offset.toFixed(2)}px`);
            });
        };

        const requestUpdate = () => {
            if (!frame) frame = window.requestAnimationFrame(update);
        };

        const handlePointer = (event) => {
            const x = event.clientX / window.innerWidth - 0.5;
            const y = event.clientY / window.innerHeight - 0.5;
            root.style.setProperty("--pointer-x", x.toFixed(3));
            root.style.setProperty("--pointer-y", y.toFixed(3));
        };

        update();
        window.addEventListener("scroll", requestUpdate, { passive: true });
        window.addEventListener("resize", requestUpdate);
        window.addEventListener("pointermove", handlePointer, { passive: true });

        return () => {
            window.removeEventListener("scroll", requestUpdate);
            window.removeEventListener("resize", requestUpdate);
            window.removeEventListener("pointermove", handlePointer);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [pageKey]);
}

function Nav({ openQuoteModal }) {
    const [lifted, setLifted] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const fn = () => setLifted(window.scrollY > 10);
        window.addEventListener("scroll", fn);
        return () => window.removeEventListener("scroll", fn);
    }, []);

    const handleHomeClick = () => {
        navigate("/");
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "auto" });
    };

    const handleBrowseClick = () => {
        navigate("/browse");
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "auto" });
    };

    const scrollToSection = (sectionId) => {
        setOpen(false);

        // If not on home page, navigate to home first
        if (location.pathname !== "/") {
            navigate("/");
            // Wait for navigation and then scroll
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    const offset = element.getBoundingClientRect().top + window.scrollY - 80;
                    window.scrollTo({ top: offset, behavior: "smooth" });
                }
            }, 100);
        } else {
            // Already on home page, just scroll
            const element = document.getElementById(sectionId);
            if (element) {
                const offset = element.getBoundingClientRect().top + window.scrollY - 80;
                window.scrollTo({ top: offset, behavior: "smooth" });
            }
        }
    };

    return (
        <>
            <nav className={`nav ${lifted ? "lifted" : ""}`}>
                <div className="nav-logo" onClick={handleHomeClick}>
                    <img src={logo} alt="SNS Building Supplies" className="logo-img" />
                </div>
                <ul className="nav-links">
                    <li><button className="nav-link" onClick={() => scrollToSection("products")}>Products</button></li>
                    <li><button className="nav-link" onClick={() => scrollToSection("trade")}>Trade</button></li>
                    <li><button className="nav-link" onClick={() => scrollToSection("about")}>About</button></li>
                    <li><button className="nav-link" onClick={() => scrollToSection("contact")}>Contact</button></li>
                    <li><button className="nav-link" onClick={handleBrowseClick}>Browse All</button></li>
                    <li><button className="nav-cta" onClick={() => { openQuoteModal(); setOpen(false); }}>Request a Quote</button></li>
                </ul>
                <button className={`hamburger ${open ? "open" : ""}`} onClick={() => setOpen(!open)} aria-label="Menu">
                    <span /><span /><span />
                </button>
            </nav>
            {open && (
                <div className="mobile-nav">
                    <button className="mob-link" onClick={() => scrollToSection("products")}>Products</button>
                    <button className="mob-link" onClick={() => scrollToSection("trade")}>Trade</button>
                    <button className="mob-link" onClick={() => scrollToSection("about")}>About</button>
                    <button className="mob-link" onClick={() => scrollToSection("contact")}>Contact</button>
                    <button className="mob-link" onClick={handleBrowseClick}>Browse All</button>
                    <button className="mob-link mob-cta" onClick={() => { openQuoteModal(); setOpen(false); }}>Request a Quote →</button>
                </div>
            )}
        </>
    );
}

function Hero({ openQuoteModal }) {
    const navigate = useNavigate();
    const { products } = useData();
    const imgs = products.length >= 10 ? [products[0]?.images?.[0] || products[0]?.img, products[3]?.images?.[0] || products[3]?.img, products[6]?.images?.[0] || products[6]?.img, products[9]?.images?.[0] || products[9]?.img].filter(Boolean) : [];

    return (
        <section className="hero" id="home">
            <div className="hero-cinematic-bg" aria-hidden="true">
                <span className="hero-orbit hero-orbit-one" data-parallax-depth="0.05" />
                <span className="hero-orbit hero-orbit-two" data-parallax-depth="-0.08" />
                <span className="hero-grain" />
            </div>
            <div className="hero-left" data-parallax-depth="-0.035">
                <span className="eyebrow">UK-Based Flooring Supplier · London E14</span>
                <h1 className="hero-h1">
                    <span className="headline-line">Complete Flooring</span>
                    <span className="headline-line"><em>Supply</em> Solutions.</span>
                </h1>
                <p className="hero-sub">SPC, LVT, Herringbone SPC, Artificial Grass and carpet sourcing — supplied to builders, contractors, and homeowners across the UK.</p>
                <div className="hero-btns">
                    <button onClick={() => openQuoteModal()} className="btn btn-dark">Request a Quote</button>
                    <button onClick={() => navigate("/browse")} className="btn btn-ghost">Browse All Products</button>
                </div>
                <MaterialMarquee items={["SPC Flooring", "LVT Planks", "Herringbone", "Artificial Grass", "Project Quotes"]} />
                <div className="hero-stats">
                    <div className="hstat"><span className="hstat-n">40</span><div className="hstat-l">Products</div></div>
                    <div className="hstat"><span className="hstat-n">5</span><div className="hstat-l">Categories</div></div>
                    <div className="hstat"><span className="hstat-n">UK</span><div className="hstat-l">Based</div></div>
                </div>
            </div>
            <div className="hero-mosaic" data-parallax-depth="0.06">
                <div className="hero-floor-plane" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                    <span />
                </div>
                {imgs.map((src, i) => (
                    <div key={i} className={`mosaic-cell mc-${i}`} data-parallax-depth={i === 0 ? "0.03" : i === 1 ? "-0.06" : "0.09"}>
                        <div className="mosaic-img" style={{ backgroundImage: `url(${src})` }} />
                    </div>
                ))}
            </div>
        </section>
    );
}

function Products() {
    const navigate = useNavigate();
    const productTypes = [
        { num: "01", name: "SPC Flooring", category: "SPC Flooring", desc: "Rigid core flooring with practical wear layers, attached-underlay options, and resilient wood-effect finishes for busy projects.", tags: ["Rigid Core", "Attached Underlay", "Wear Layer"] },
        { num: "02", name: "LVT Flooring", category: "LVT Flooring", desc: "Luxury vinyl planks in clean wood tones, available for sample and project price requests.", tags: ["Straight Plank", "2.5mm–3.0mm", "Easy Enquiry"] },
        { num: "03", name: "Herringbone SPC", category: "Herringbone SPC Flooring", desc: "Classic herringbone layouts with SPC performance for premium residential and commercial interiors.", tags: ["Herringbone", "6mm", "IXPE"] },
        { num: "04", name: "Artificial Grass", category: "Artificial Grass", desc: "Synthetic grass options across multiple pile heights, widths, and densities for outdoor and landscaping work.", tags: ["20–40mm", "Multiple Widths", "Garden Ready"] },
        { num: "05", name: "Carpet", category: "Carpet", desc: "Carpet details are coming shortly. Enquire with SNS for current sourcing and project options.", tags: ["Coming Soon", "Residential", "Trade"] },
    ];

    const openCategory = (category) => {
        scrollToPageTop();
        navigate(`/browse?category=${encodeURIComponent(category)}`);
    };

    return (
        <section className="products-section" id="products">
            <div className="products-header-wrap">
                <span className="eyebrow">What We Supply</span>
                <h2 className="section-h2">Our Flooring Range</h2>
                <div className="rule" />
                <p className="section-sub">From precision-engineered laminate to bespoke carpeting — everything you need for a complete installation, in one place.</p>
            </div>
            <MaterialMarquee items={["SPC Flooring", "LVT Flooring", "Herringbone SPC", "Artificial Grass", "Carpet Sourcing"]} />
            <div className="products-type-grid">
                {productTypes.map((product) => (
                    <button key={product.num} type="button" className="prod-type-card" onClick={() => openCategory(product.category)} aria-label={`Browse ${product.name}`}>
                        <span className="prod-num">{product.num}</span>
                        <h3 className="prod-type-name">{product.name}</h3>
                        <p className="prod-type-desc">{product.desc}</p>
                        <div className="prod-type-tags">
                            {product.tags.map((tag) => (
                                <span key={tag} className="prod-type-tag">{tag}</span>
                            ))}
                        </div>
                    </button>
                ))}
            </div>
        </section>
    );
}

function AccessoriesBar() {
    const accessories = ["Underlay", "Trims & Beading", "Skirting Boards", "Adhesives", "Gripper Rods", "Threshold Strips", "Expansion Gaps"];

    return (
        <div className="acc-bar">
            <div className="acc-bar-inner">
                <div className="acc-title">All <em>Accessories</em> Included</div>
                <div className="acc-chips">
                    {accessories.map((acc) => (
                        <span key={acc} className="acc-chip">{acc}</span>
                    ))}
                </div>
            </div>
        </div>
    );
}

function FlooringTypesHome() {
    const navigate = useNavigate();
    const { products, categories } = useData();
    const [activeCategory, setActiveCategory] = useState("All");
    const [search, setSearch] = useState("");

    const categoryNames = categories.map(c => c.name);

    const filtered = products.filter((p) => {
        const c = activeCategory === "All" || p.category === activeCategory;
        const q = search.toLowerCase();
        const s = !q || p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.tags.some((t) => t.toLowerCase().includes(q));
        return c && s;
    });

    return (
        <section className="flooring-types-section">
            <div className="types-header">
                <span className="eyebrow">Our Collection</span>
                <h2 className="section-h2">Flooring Catalogue</h2>
                <div className="rule" />
                <p className="section-sub">Browse our complete range. Click any product to view full specifications and details.</p>
            </div>
            <div className="filter-row">
                <div className="chips">
                    {["All", ...categoryNames].map((c) => (
                        <button key={c} className={`chip ${activeCategory === c ? "chip-active" : ""}`} onClick={() => setActiveCategory(c)}>{c}</button>
                    ))}
                </div>
                <div className="search-box">
                    <span className="s-icon">⌕</span>
                    <input className="s-input" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
            </div>
            {filtered.length === 0 ? (
                <p className="no-res">No products found.</p>
            ) : (
                <div className="products-grid">
                    {filtered.map((product) => (
                        <article key={product.id} className="product-card" onClick={() => navigate(`/product/${generateSlug(product.name)}`)}>
                            <div className="product-img-wrap">
                                <img src={product.images?.[0] || product.img} alt={product.name} className="product-img" loading="lazy" />
                                <div className="product-overlay">
                                    <span className="product-view">View Details</span>
                                </div>
                                <div className="product-cat">{product.category}</div>
                            </div>
                            <div className="product-body">
                                <span className="product-subtitle">{product.subtitle}</span>
                                <h3 className="product-name">{product.name}</h3>
                                <div className="product-bar" style={{ background: product.accent }} />
                                <p className="product-desc">{product.description.slice(0, 105)}…</p>
                                <div className="product-tags">
                                    {product.tags.slice(0, 3).map((t) => <span key={t} className="ptag">{t}</span>)}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </section>
    );
}

function About() {
    const audiences = ["Builders & Contractors", "Flooring Installers", "Property Developers", "Landlords", "Homeowners"];

    return (
        <section className="about-section" id="about">
            <div className="about-grid">
                <div>
                    <span className="eyebrow">About Us</span>
                    <h2 className="section-h2">Built on Reliability & Service</h2>
                    <div className="rule" />
                    <div className="vm-cards">
                        <div className="vm-card">
                            <h4 className="vm-card-title">Our Vision</h4>
                            <p className="vm-card-text">To become a trusted and reliable building materials supplier, known for delivering complete flooring solutions with quality, consistency, and excellent service.</p>
                        </div>
                        <div className="vm-card">
                            <h4 className="vm-card-title">Our Mission</h4>
                            <p className="vm-card-text">To provide builders, contractors, landlords, and homeowners with efficient and dependable flooring supply solutions — ensuring every project has the right materials at the right time.</p>
                        </div>
                    </div>
                </div>
                <div className="about-body">
                    <p className="about-p">SNS Building Supplies Limited is a UK-based business specialising in the supply of flooring materials and all necessary installation accessories. We provide complete flooring solutions so our customers can source everything they need in one place.</p>
                    <p className="about-p">Whether you're a property developer fitting out multiple units, a contractor working to a tight programme, or a homeowner renovating a single room — we have the materials and the expertise to support you from start to finish.</p>
                    <p className="about-p">At this stage the business operates as a supply service. Our focus is on building long-term relationships through reliability, honest pricing and attentive service.</p>
                    <div className="audience-wrap">
                        <span className="eyebrow">Who We Work With</span>
                        <div className="audience-chips">
                            {audiences.map((aud) => (
                                <span key={aud} className="audience-chip">{aud}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Trade({ openQuoteModal }) {
    return (
        <section className="trade" id="trade">
            <div className="trade-inner">
                <div>
                    <span className="eyebrow">For Professionals</span>
                    <h2 className="section-h2">Trade Supply Partners</h2>
                    <div className="rule" />
                    <p className="section-sub" style={{marginBottom:"2rem"}}>We work directly with builders, contractors, flooring installers and property developers across the UK. Our service is built around your project schedules and volume requirements.</p>
                    <div className="feats">
                        {[
                            ["01","Reliable, Fast Supply","Materials sourced and dispatched on time — keeping your projects on schedule."],
                            ["02","Complete Material Lists","Flooring and every installation accessory in one order."],
                            ["03","Volume & Multi-Site","Competitive pricing for developers managing multiple properties."],
                            ["04","Dedicated Account Support","A single contact for quotes, orders, and technical queries."],
                        ].map(([n,h,p]) => (
                            <div key={n} className="feat">
                                <div className="feat-n">{n}</div>
                                <div><h4 className="feat-h">{h}</h4><p className="feat-p">{p}</p></div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="trade-box">
                    <h3 className="tbox-h">Open a Trade Account</h3>
                    <p className="tbox-p">Property developers and interior designers benefit from priority fulfilment, full technical specifications and dedicated account management.</p>
                    <button onClick={() => openQuoteModal()} className="btn btn-dark" style={{display:"block",textAlign:"center",width:"100%"}}>Submit a Trade Enquiry</button>
                    <p className="tbox-tel">Or call us: <a href="tel:+447343069646" className="tel-link">+44 7343 069 646</a></p>
                </div>
            </div>
        </section>
    );
}

const getProductKeySpecs = (product, limit = 3) => {
    const labelMap = {
        size: "Size",
        thickness: "Thickness",
        widths: "Widths",
        density: "Density",
        wearLayer: "Wear Layer",
        packCoverage: "Pack Coverage",
        underlay: "Underlay",
        planksPerBox: "Planks / Box",
    };

    return Object.entries(product.measurements || {})
        .filter(([, value]) => value)
        .slice(0, limit)
        .map(([key, value]) => [labelMap[key] || key.replace(/([A-Z])/g, " $1"), value]);
};

const getProductSurfaceClass = (category = "") => {
    if (category.includes("Artificial Grass")) return "surface-grass";
    if (category.includes("Herringbone")) return "surface-herringbone";
    if (category.includes("LVT")) return "surface-lvt";
    if (category.includes("SPC")) return "surface-spc";
    return "surface-neutral";
};

function ProductActionButtons({ product, openQuoteModal, compact = false }) {
    const message = encodeURIComponent(`Hi SNS Building Supplies, I would like to enquire about ${product.name} (${product.productCode || product.category}).`);
    const whatsappUrl = `https://wa.me/447343069646?text=${message}`;

    const handleQuoteClick = (event) => {
        event.stopPropagation();
        openQuoteModal(product);
    };

    return (
        <div className={`product-actions ${compact ? "product-actions-compact" : ""}`}>
            <button type="button" className="product-action-btn product-action-primary" onClick={handleQuoteClick}>
                Request Price
            </button>
            <button type="button" className="product-action-btn" onClick={handleQuoteClick}>
                Request Sample
            </button>
            <a className="product-action-btn product-action-whatsapp" href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>
                Enquire Now
            </a>
        </div>
    );
}

function ProductLoadingScreen() {
    return (
        <main className="product-loading-screen">
            <span className="eyebrow">Preparing Catalogue</span>
            <h1>Loading products</h1>
        </main>
    );
}

function ProductCard({ product, openQuoteModal }) {
    const navigate = useNavigate();
    const surfaceClass = getProductSurfaceClass(product.category);

    return (
        <article className={`product-card ${surfaceClass}`} style={{ "--card-i": product.index || 0 }} onClick={() => navigate(`/product/${generateSlug(product.name)}`)}>
            <div className="product-img-wrap">
                <img src={product.images?.[0] || product.img} alt={product.name} className="product-img" loading="lazy" />
                <div className="product-overlay">
                    <span className="product-view">View Details</span>
                </div>
                <div className="product-cat">{product.category}</div>
            </div>
            <div className="product-body">
                <span className="product-material-line" aria-hidden="true" />
                <div className="product-card-head">
                    <span className="product-subtitle">{product.subtitle}</span>
                    <span className="product-status">{product.status || "Available on Request"}</span>
                </div>
                <h3 className="product-name">{product.name}</h3>
                <div className="product-bar" style={{ background: product.accent }} />
                <div className="product-card-specs">
                    {getProductKeySpecs(product).map(([label, value]) => (
                        <div key={label} className="product-card-spec">
                            <span>{label}</span>
                            <strong>{value}</strong>
                        </div>
                    ))}
                </div>
                <div className="product-tags">
                    {(product.tags || []).slice(0, 3).map((t) => <span key={t} className="ptag">{t}</span>)}
                </div>
                <ProductActionButtons product={product} openQuoteModal={openQuoteModal} compact />
            </div>
        </article>
    );
}

function BrowseAllProducts({ openQuoteModal }) {
    const { products, categories } = useData();
    const location = useLocation();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    useLayoutEffect(() => {
        scrollToPageTop();
    }, [location.search]);

    const categoryNames = categories.map(c => c.name);
    const selectedCategory = new URLSearchParams(location.search).get("category");
    const activeCategory = categoryNames.includes(selectedCategory) ? selectedCategory : "All";
    const heroProducts = products.slice(0, 3);
    const productTotal = products.length || "All";
    const categoryTotal = categoryNames.length || "Multiple";

    const filtered = products.filter((p) => {
        const c = activeCategory === "All" || p.category === activeCategory;
        const q = search.toLowerCase();
        const s = !q || p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.tags.some((t) => t.toLowerCase().includes(q));
        return c && s;
    });

    const setCategoryFilter = (category) => {
        scrollToPageTop();
        if (category === "All") {
            navigate("/browse");
            return;
        }
        navigate(`/browse?category=${encodeURIComponent(category)}`);
    };

    return (
        <>
            <section className="browse-all-view">
                <div className="browse-hero">
                    <div className="browse-hero-copy">
                        <span className="eyebrow">Our Collection</span>
                        <h1 className="browse-title"><AnimatedWords text="Flooring Catalogue" /></h1>
                        <p className="browse-sub">Browse every finish, texture, and installation-ready flooring option from one focused product library.</p>
                        <div className="browse-metrics">
                            <span><strong>{productTotal}</strong> Products</span>
                            <span><strong>{categoryTotal}</strong> Categories</span>
                            <span><strong>UK</strong> Supply</span>
                        </div>
                    </div>
                    <div className="browse-visual" aria-hidden="true" data-parallax-depth="0.05">
                        <span className="browse-ring browse-ring-one" />
                        <span className="browse-ring browse-ring-two" />
                        <div className="browse-stack">
                            {(heroProducts.length ? heroProducts : [
                                { id: "fallback-1", img: "", images: [] },
                                { id: "fallback-2", img: "", images: [] },
                                { id: "fallback-3", img: "", images: [] },
                            ]).map((product, index) => (
                                <div
                                    key={product.id}
                                    className={`browse-tile browse-tile-${index} ${heroProducts.length ? "" : "browse-tile-fallback"}`}
                                    style={{ backgroundImage: `url(${product.images?.[0] || product.img})` }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <MaterialMarquee items={activeCategory === "All" ? categoryNames : [activeCategory, "Request Price", "Request Sample", "Enquire Now"]} />
                <div className="browse-filter-panel">
                    <div>
                        <span className="filter-label">Refine Products</span>
                        <div className="chips">
                            {["All", ...categoryNames].map((c) => (
                                <button key={c} className={`chip ${activeCategory === c ? "chip-active" : ""}`} onClick={() => setCategoryFilter(c)}>{c}</button>
                            ))}
                        </div>
                    </div>
                    <div className="search-box">
                        <span className="s-icon">⌕</span>
                        <input className="s-input" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>
                <div className="browse-results-bar">
                    <span>{filtered.length} matching {filtered.length === 1 ? "product" : "products"}</span>
                    <span>{activeCategory === "All" ? "All flooring types" : activeCategory}</span>
                </div>
                {filtered.length === 0 ? (
                    <p className="no-res">No products found.</p>
                ) : (
                    <div className="products-grid">
                        {filtered.map((product, index) => (
                            <ProductCard key={product.id} product={{ ...product, index }} openQuoteModal={openQuoteModal} />
                        ))}
                    </div>
                )}
            </section>
            <Contact />
        </>
    );
}

function CategoryView({ openQuoteModal }) {
    const { categoryName } = useParams();
    const { products, categories } = useData();
    const category = decodeURIComponent(categoryName);
    const categoryProducts = products.filter(p => p.category === category);
    const categoryType = categories.find(t => t.name === category);

    return (
        <>
            <section className="category-view">
                <div className="category-header">
                    <span className="eyebrow">{category} Collection</span>
                    <h2 className="section-h2">{category} Flooring</h2>
                    <div className="rule" style={{ background: categoryType?.accent }} />
                    <p className="section-sub">{categoryType?.description}</p>
                    <p className="product-count">{categoryProducts.length} {categoryProducts.length === 1 ? 'Product' : 'Products'} Available</p>
                </div>
                <MaterialMarquee items={[category, "Available on Request", "Request Sample", "Project Pricing", "WhatsApp Enquiry"]} />
                <div className="products-grid">
                    {categoryProducts.map((product, index) => (
                        <ProductCard key={product.id} product={{ ...product, index }} openQuoteModal={openQuoteModal} />
                    ))}
                </div>
            </section>
            <Contact />
        </>
    );
}

function ProductDetailView({ openQuoteModal }) {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { products } = useData();
    const product = products.find(p => generateSlug(p.name) === slug);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [expandedSection, setExpandedSection] = useState(null);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [reviewForm, setReviewForm] = useState({
        customerName: '',
        rating: 5,
        title: '',
        review: ''
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
        setSelectedImageIndex(0);
        setExpandedSection(null);
        setShowFullDescription(false);
        fetchReviews();
    }, [slug]);

    const fetchReviews = async () => {
        if (!product) return;
        try {
            const { getReviews } = await import('./api/api');
            const data = await getReviews(product.id);
            // Only show approved reviews
            setReviews(data.filter(r => r.status === 'approved'));
        } catch (error) {
            console.error('Failed to fetch reviews:', error);
        }
    };

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        try {
            const { createReview } = await import('./api/api');
            await createReview({
                ...reviewForm,
                productId: product.id
            });
            setReviewForm({ customerName: '', rating: 5, title: '', review: '' });
            setShowReviewForm(false);
            alert('Thank you! Your review has been submitted and is pending approval.');
        } catch (error) {
            alert('Failed to submit review: ' + error.message);
        }
    };

    const renderStars = (rating) => {
        return '★'.repeat(rating) + '☆'.repeat(5 - rating);
    };

    const averageRating = reviews.length > 0
        ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
        : 0;

    if (!product) {
        return <div>Product not found</div>;
    }

    // Support both old img format and new images array
    const productImages = product.images || (product.img ? [product.img] : []);
    const currentImage = productImages[selectedImageIndex] || productImages[0];

    const specs = [
        ["Width", product.width],
        ["Length", product.length],
        ["Finish", product.finish],
        ["Pile", product.pile],
        ["Weight", product.weight],
        ["Density", product.density],
        ["Roll Width", product.rollWidth],
        ["Suitability", product.suitability],
    ].filter(([, v]) => v);

    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    const productCode = product.productCode || `SNS${product.id.toString().padStart(4, '0')}`;
    const descriptionPreview = product.description.length > 200
        ? product.description.substring(0, 200) + '...'
        : product.description;
    const detailSpecs = getProductKeySpecs(product, 6);
    const surfaceClass = getProductSurfaceClass(product.category);

    return (
        <>
            <section className={`product-detail-view ${surfaceClass}`}>
                {/* Breadcrumb Navigation */}
                <div className="breadcrumb">
                    <span onClick={() => navigate('/')} className="breadcrumb-link">HOME</span>
                    <span className="breadcrumb-separator">›</span>
                    <span onClick={() => navigate('/browse')} className="breadcrumb-link">PRODUCTS</span>
                    <span className="breadcrumb-separator">›</span>
                    <span onClick={() => navigate(`/category/${product.category}`)} className="breadcrumb-link">{product.category.toUpperCase()}</span>
                    <span className="breadcrumb-separator">›</span>
                    <span className="breadcrumb-current">{product.name.toUpperCase()}</span>
                </div>

                <div className="product-detail-grid">
                    <div className="product-images-section">
                        <div className="product-detail-img-wrap">
                            <img src={currentImage} alt={product.name} className="product-detail-img" />
                            <div className="product-detail-badge">{product.category}</div>
                        </div>
                        {productImages.length > 1 && (
                            <div className="product-image-gallery">
                                {productImages.map((img, index) => (
                                    <div
                                        key={index}
                                        onClick={() => setSelectedImageIndex(index)}
                                        className={`gallery-thumbnail ${selectedImageIndex === index ? 'gallery-thumbnail-active' : ''}`}
                                    >
                                        <img
                                            src={img}
                                            alt={`${product.name} - ${index + 1}`}
                                        />
                                    </div>
                                ))}
                                {productImages.length > 4 && (
                                    <div className="gallery-more">
                                        + {productImages.length - 3} More
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="product-detail-content">
                        <div className="product-detail-summary">
                            <div>
                                <span className="product-code">PRODUCT CODE: {productCode}</span>
                                <div className="product-detail-status">{product.status || "Available on Request"}</div>
                            </div>
                            <span className="product-detail-category">{product.category}</span>
                        </div>
                        <h1 className="product-detail-title"><AnimatedWords text={product.name} /></h1>
                        <MaterialMarquee items={[product.category, product.status || "Available on Request", productCode, "Request Sample", "WhatsApp Enquiry"]} />

                        <div className="product-description">
                            <p>{showFullDescription ? product.description : descriptionPreview}</p>
                            {product.description.length > 200 && (
                                <button
                                    className="read-more-btn"
                                    onClick={() => setShowFullDescription(!showFullDescription)}
                                >
                                    {showFullDescription ? 'Read Less' : 'Read More'}
                                </button>
                            )}
                        </div>

                        {detailSpecs.length > 0 && (
                            <div className="product-detail-spec-strip">
                                {detailSpecs.map(([label, value]) => (
                                    <div key={label} className="product-detail-spec-pill">
                                        <span>{label}</span>
                                        <strong>{value}</strong>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="product-enquiry-panel">
                            <div>
                                <span className="filter-label">Fast Enquiry</span>
                                <p>Ask for trade pricing, sample availability, or a project quote for this product.</p>
                            </div>
                            <ProductActionButtons product={product} openQuoteModal={openQuoteModal} />
                        </div>

                        <div className="product-accordions">
                            {/* Features */}
                            {product.features && product.features.length > 0 && (
                                <div className="accordion-item">
                                    <button
                                        className={`accordion-header ${expandedSection === 'features' ? 'active' : ''}`}
                                        onClick={() => toggleSection('features')}
                                    >
                                        <span>Features</span>
                                        <span className="accordion-icon">›</span>
                                    </button>
                                    {expandedSection === 'features' && (
                                        <div className="accordion-content">
                                            <ul className="features-list">
                                                {product.features.map((feature, idx) => (
                                                    <li key={idx}>{feature}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Specifications */}
                            {specs.length > 0 && (
                                <div className="accordion-item">
                                    <button
                                        className={`accordion-header ${expandedSection === 'specifications' ? 'active' : ''}`}
                                        onClick={() => toggleSection('specifications')}
                                    >
                                        <span>Specifications</span>
                                        <span className="accordion-icon">›</span>
                                    </button>
                                    {expandedSection === 'specifications' && (
                                        <div className="accordion-content">
                                            <div className="specs-grid">
                                                {specs.map(([k, v]) => (
                                                    <div key={k} className="spec-row">
                                                        <span className="spec-key">{k}</span>
                                                        <span className="spec-value">{v}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Product Measurements */}
                            {product.measurements && Object.keys(product.measurements).length > 0 && (
                                <div className="accordion-item">
                                    <button
                                        className={`accordion-header ${expandedSection === 'measurements' ? 'active' : ''}`}
                                        onClick={() => toggleSection('measurements')}
                                    >
                                        <span>Product Measurements</span>
                                        <span className="accordion-icon">›</span>
                                    </button>
                                    {expandedSection === 'measurements' && (
                                        <div className="accordion-content">
                                            <div className="measurements-info">
                                                {Object.entries(product.measurements).map(([key, value]) => (
                                                    <div key={key} style={{ marginBottom: '0.5rem' }}>
                                                        <strong style={{ textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}:</strong> {value}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Guarantees & Accreditations */}
                            {product.guarantees && product.guarantees.length > 0 && (
                                <div className="accordion-item">
                                    <button
                                        className={`accordion-header ${expandedSection === 'guarantees' ? 'active' : ''}`}
                                        onClick={() => toggleSection('guarantees')}
                                    >
                                        <span>Guarantees & Accreditations</span>
                                        <span className="accordion-icon">›</span>
                                    </button>
                                    {expandedSection === 'guarantees' && (
                                        <div className="accordion-content">
                                            <ul className="guarantees-list">
                                                {product.guarantees.map((guarantee, idx) => (
                                                    <li key={idx}>{guarantee}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Installation */}
                            {product.installation && (
                                <div className="accordion-item">
                                    <button
                                        className={`accordion-header ${expandedSection === 'installation' ? 'active' : ''}`}
                                        onClick={() => toggleSection('installation')}
                                    >
                                        <span>Installation</span>
                                        <span className="accordion-icon">›</span>
                                    </button>
                                    {expandedSection === 'installation' && (
                                        <div className="accordion-content">
                                            <div className="installation-info">
                                                {product.installation}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Documents */}
                            {product.documents && product.documents.length > 0 && (
                                <div className="accordion-item">
                                    <button
                                        className={`accordion-header ${expandedSection === 'documents' ? 'active' : ''}`}
                                        onClick={() => toggleSection('documents')}
                                    >
                                        <span>Documents</span>
                                        <span className="accordion-icon">›</span>
                                    </button>
                                    {expandedSection === 'documents' && (
                                        <div className="accordion-content">
                                            <ul className="documents-list">
                                                {product.documents.map((doc, idx) => (
                                                    <li key={idx}>
                                                        <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                                            {doc.name}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            </section>

            {/* Similar Products Section (Automated - Same Category) */}
            {(() => {
                const similarProducts = products
                    .filter(p =>
                        p.category === product.category &&
                        p.id !== product.id &&
                        !(product.suggestedProducts || []).includes(p.id)
                    )
                    .slice(0, 4);

                return similarProducts.length > 0 && (
                    <section className="suggested-products-section" style={{ background: 'var(--champagne)' }}>
                        <div className="section-container">
                            <h2 className="section-title">Similar Products</h2>
                            <p style={{ color: 'var(--mid)', marginTop: '0.5rem', marginBottom: '2rem' }}>
                                More {product.category} options you might like
                            </p>
                            <div className="suggested-products-grid">
                                {similarProducts.map(similarProduct => (
                                    <div key={similarProduct.id} className="suggested-product-card">
                                        <div
                                            className="suggested-product-image"
                                            onClick={() => navigate(`/product/${generateSlug(similarProduct.name)}`)}
                                        >
                                            <img src={similarProduct.images?.[0] || similarProduct.img} alt={similarProduct.name} />
                                        </div>
                                        <div className="suggested-product-content">
                                            <h3 className="suggested-product-title">{similarProduct.name}</h3>
                                            <p className="suggested-product-subtitle">{similarProduct.subtitle}</p>
                                            <button
                                                onClick={() => navigate(`/product/${generateSlug(similarProduct.name)}`)}
                                                className="btn-view-product"
                                            >
                                                View Details →
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                );
            })()}

            {/* Reviews Section */}
            <section className="reviews-section">
                <div className="section-container">
                    <div className="reviews-header">
                        <h2 className="section-title">Customer Reviews</h2>
                        {reviews.length > 0 && (
                            <div className="reviews-summary">
                                <div className="average-rating">
                                    <span className="rating-number">{averageRating}</span>
                                    <span className="rating-stars">{renderStars(Math.round(averageRating))}</span>
                                    <span className="rating-count">Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
                                </div>
                            </div>
                        )}
                        <button
                            onClick={() => setShowReviewForm(!showReviewForm)}
                            className="btn-write-review"
                        >
                            {showReviewForm ? 'Cancel' : 'Write a Review'}
                        </button>
                    </div>

                    {showReviewForm && (
                        <form onSubmit={handleReviewSubmit} className="review-form">
                            <div className="form-group">
                                <label>Your Name *</label>
                                <input
                                    type="text"
                                    value={reviewForm.customerName}
                                    onChange={(e) => setReviewForm({...reviewForm, customerName: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Rating *</label>
                                <div className="rating-input">
                                    {[5, 4, 3, 2, 1].map(star => (
                                        <label key={star} className="star-label">
                                            <input
                                                type="radio"
                                                name="rating"
                                                value={star}
                                                checked={reviewForm.rating === star}
                                                onChange={(e) => setReviewForm({...reviewForm, rating: parseInt(e.target.value)})}
                                            />
                                            <span>{renderStars(star)}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Review Title *</label>
                                <input
                                    type="text"
                                    value={reviewForm.title}
                                    onChange={(e) => setReviewForm({...reviewForm, title: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Your Review *</label>
                                <textarea
                                    value={reviewForm.review}
                                    onChange={(e) => setReviewForm({...reviewForm, review: e.target.value})}
                                    rows="5"
                                    required
                                />
                            </div>
                            <button type="submit" className="btn-submit-review">Submit Review</button>
                        </form>
                    )}

                    <div className="reviews-list">
                        {reviews.length === 0 && !showReviewForm && (
                            <p className="no-reviews">No reviews yet. Be the first to review this product!</p>
                        )}
                        {reviews.map(review => (
                            <div key={review.id} className="review-item">
                                <div className="review-header">
                                    <div className="review-author">{review.customerName}</div>
                                    <div className="review-rating">{renderStars(review.rating)}</div>
                                    <div className="review-date">{review.date}</div>
                                </div>
                                <h4 className="review-title">{review.title}</h4>
                                <p className="review-text">{review.review}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Suggested Products Section */}
            {product.suggestedProducts && product.suggestedProducts.length > 0 && (
                <section className="suggested-products-section">
                    <div className="section-container">
                        <h2 className="section-title">You May Also Like</h2>
                        <div className="suggested-products-grid">
                            {product.suggestedProducts.map(suggestedId => {
                                const suggestedProduct = products.find(p => p.id === suggestedId);
                                if (!suggestedProduct) return null;
                                return (
                                    <div key={suggestedProduct.id} className="suggested-product-card">
                                        <div
                                            className="suggested-product-image"
                                            onClick={() => navigate(`/product/${generateSlug(suggestedProduct.name)}`)}
                                        >
                                            <img src={suggestedProduct.images?.[0] || suggestedProduct.img} alt={suggestedProduct.name} />
                                        </div>
                                        <div className="suggested-product-content">
                                            <h3 className="suggested-product-title">{suggestedProduct.name}</h3>
                                            <p className="suggested-product-subtitle">{suggestedProduct.subtitle}</p>
                                            <button
                                                onClick={() => navigate(`/product/${generateSlug(suggestedProduct.name)}`)}
                                                className="btn-view-product"
                                            >
                                                View Details →
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            <Contact />
        </>
    );
}

function Contact() {
    return (
        <>
            <div className="div-line" />
            <section className="contact-sec" id="contact">
                <div className="contact-grid">
                    <div>
                        <div className="foot-logo">
                            <img src={logo} alt="SNS Building Supplies" className="logo-img" />
                        </div>
                        <p className="foot-tag">Complete Flooring Supply Solutions<br />London, United Kingdom</p>
                    </div>
                    <div className="ccol">
                        <h4 className="ccol-h">Get In Touch</h4>
                        <a href="mailto:enquiries@snsbuildingsupplies.com" className="clink">enquiries@snsbuildingsupplies.com</a>
                        <a href="mailto:sales@snsbuildingsupplies.com" className="clink">sales@snsbuildingsupplies.com</a>
                        <a href="tel:+447343069646" className="clink">+44 7343 069 646</a>
                    </div>
                    <div className="ccol">
                        <h4 className="ccol-h">Our Address</h4>
                        <p className="clink">22 Julius House, Blair Street</p>
                        <p className="clink">London, E14 0NN</p>
                        <p className="clink">United Kingdom</p>
                    </div>
                </div>
            </section>
            <footer className="footer">
                <p>© 2026 SNS BUILDING SUPPLIES LIMITED. ALL RIGHTS RESERVED.</p>
                <p>LONDON · E14 0NN</p>
            </footer>
        </>
    );
}

function HomePage({ openQuoteModal }) {
    return (
        <>
            <Hero openQuoteModal={openQuoteModal} />
            <Products />
            <AccessoriesBar />
            <Trade openQuoteModal={openQuoteModal} />
            <About />
            <Contact />
        </>
    );
}

function ProtectedRoute({ children }) {
    const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('adminToken'));

    const handleLogin = (token) => {
        setIsAuthenticated(true);
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
    };

    if (!isAuthenticated) {
        return <AdminLogin onLogin={handleLogin} />;
    }

    return typeof children === 'function' ? children(handleLogout) : children;
}

function AppRoutes() {
    const [quoteModalOpen, setQuoteModalOpen] = useState(false);
    const [quoteProduct, setQuoteProduct] = useState(null);
    const location = useLocation();
    const { loading, error } = useData();

    useScrollReveal(location.pathname);
    useCinematicParallax(location.pathname);

    useLayoutEffect(() => {
        if (location.pathname.startsWith("/product/")) {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        }
    }, [location.pathname]);

    const openQuoteModal = (product = null) => {
        setQuoteProduct(product);
        setQuoteModalOpen(true);
    };

    const closeQuoteModal = () => {
        setQuoteModalOpen(false);
        setQuoteProduct(null);
    };

    if (loading) {
        return (
            <>
                <Nav openQuoteModal={openQuoteModal} />
                <ProductLoadingScreen />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Nav openQuoteModal={openQuoteModal} />
                <main className="product-loading-screen">
                    <span className="eyebrow">Catalogue Unavailable</span>
                    <h1>Please refresh the page</h1>
                </main>
            </>
        );
    }

    return (
        <>
            <Routes>
                {/* Public routes */}
                <Route path="/" element={<><Nav openQuoteModal={openQuoteModal} /><HomePage openQuoteModal={openQuoteModal} /></>} />
                <Route path="/browse" element={<><Nav openQuoteModal={openQuoteModal} /><BrowseAllProducts openQuoteModal={openQuoteModal} /></>} />
                <Route path="/category/:categoryName" element={<><Nav openQuoteModal={openQuoteModal} /><CategoryView openQuoteModal={openQuoteModal} /></>} />
                <Route path="/product/:slug" element={<><Nav openQuoteModal={openQuoteModal} /><ProductDetailView openQuoteModal={openQuoteModal} /></>} />

                {/* Admin routes */}
            <Route
                path="/admin"
                element={
                    <ProtectedRoute>
                        {(onLogout) => <Navigate to="/admin/products" replace />}
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/login"
                element={<AdminLogin onLogin={(token) => {}} />}
            />
            <Route
                path="/admin/products"
                element={
                    <ProtectedRoute>
                        {(onLogout) => <AdminProducts onLogout={onLogout} />}
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/categories"
                element={
                    <ProtectedRoute>
                        {(onLogout) => <AdminCategories onLogout={onLogout} />}
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/quoterequests"
                element={
                    <ProtectedRoute>
                        {(onLogout) => <AdminQuoteRequests onLogout={onLogout} />}
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/reviews"
                element={
                    <ProtectedRoute>
                        {(onLogout) => <AdminReviews onLogout={onLogout} />}
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/content"
                element={
                    <ProtectedRoute>
                        {(onLogout) => <AdminContent onLogout={onLogout} />}
                    </ProtectedRoute>
                }
            />
            </Routes>

            <QuoteRequestForm
                isOpen={quoteModalOpen}
                onClose={closeQuoteModal}
                product={quoteProduct}
            />
        </>
    );
}

export default function App() {
    return (
        <DataProvider>
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
        </DataProvider>
    );
}
